<template>
  <div class="modern-form-container">
    <!-- Main Content Card -->
    <div class="form-card">
      <div class="card-header">
        <div class="header-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10,9 9,9 8,9" />
          </svg>
        </div>
        <h2 class="form-title">
          {{ t('admin-ui.stepper_summary') }}
        </h2>
        <p class="form-subtitle">
          {{ t('admin-ui.summary_form_below_find_summary_user_and_company') }}
        </p>
      </div>

      <div class="card-body">
        <!-- Action Section -->
        <div class="action-section">
          <div class="action-highlight">
            <div class="action-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 60 60"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <g transform="translate(-27.164465,-22.917748)">
                  <path
                    d="m 0,0 h -2.667 c -2.37,0 -4.292,-1.922 -4.292,-4.292 v -40.531 c 9.254,-5.642 15.466,-15.801 15.466,-27.409 0,-11.609 -6.212,-21.766 -15.466,-27.408 v -40.531 c 0,-2.37 1.922,-4.292 4.292,-4.292 h 34.965 c 2.37,0 4.292,1.922 4.292,4.292 V -36.59 C 36.59,-16.382 20.208,0 0,0"
                    fill="currentColor"
                    transform="matrix(0.35277776,0,0,-0.35277776,65.722366,27.436081)"
                  />
                  <path
                    d="M 0,0 C 0,11.606 6.209,21.762 15.459,27.405 V 67.94 c 0,2.371 -1.922,4.292 -4.292,4.292 h -34.959 c -2.37,0 -4.292,-1.921 -4.292,-4.292 v -96.626 c 0,-22.419 16.942,-40.882 38.722,-43.281 2.561,-0.282 4.821,1.715 4.821,4.292 v 40.272 C 6.209,-21.761 0,-11.605 0,0"
                    fill="currentColor"
                    transform="matrix(0.35277776,0,0,-0.35277776,46.067715,52.959801)"
                  />
                  <path
                    d="m 0,0 c -3.539,5.468 -9.662,9.112 -16.645,9.112 -7.015,0 -13.158,-3.682 -16.689,-9.193 -1.976,-3.086 -3.159,-6.728 -3.159,-10.657 0,-3.927 1.183,-7.57 3.159,-10.655 3.531,-5.51 9.674,-9.193 16.689,-9.193 6.983,0 13.106,3.645 16.645,9.112 2.007,3.101 3.203,6.776 3.203,10.736 C 3.203,-6.776 2.007,-3.101 0,0"
                    fill="currentColor"
                    transform="matrix(0.35277776,0,0,-0.35277776,63.267456,49.129973)"
                  />
                </g>
              </svg>
            </div>
            <div class="action-info">
              <h4>
                {{ t('admin-ui.summary_form_ready_to_create_your_account') }}
              </h4>
            </div>
          </div>

          <div class="action-buttons">
            <button
              class="back-button"
              :disabled="isAccountCreationInProgress"
              @click="$emit('previousClick')"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M15 18L9 12L15 6" />
              </svg>
              {{ t('admin-ui.back') }}
            </button>

            <button
              class="create-button"
              :disabled="disableCreateAccountButton || companyCreationIsPending"
              @click="createAccount()"
            >
              <div v-if="companyCreationIsPending" class="spinner" />
              {{
                companyCreationIsPending
                  ? t('admin-ui.creating_account')
                  : t('admin-ui.create-account')
              }}
            </button>
          </div>
        </div>

        <!-- User Information Section -->
        <div class="info-section">
          <div class="section-header">
            <div class="section-icon user-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h3 class="section-title">{{ t('admin-ui.user') }}</h3>
          </div>

          <div class="form-grid">
            <div class="input-group full-width">
              <hit-input
                name="email"
                :value="objectFormData.usernameEmail || ''"
                :label="t('admin-ui.email')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="firstName"
                :value="objectFormData.firstName || ''"
                :label="t('admin-ui.first_name')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="lastName"
                :value="objectFormData.lastName || ''"
                :label="t('admin-ui.last_name')"
                :disabled="true"
              />
            </div>
          </div>
        </div>

        <!-- Company Information Section -->
        <div class="info-section">
          <div class="section-header">
            <div class="section-icon company-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
                <line x1="9" y1="9" x2="9" y2="9.01" />
                <line x1="9" y1="12" x2="9" y2="12.01" />
                <line x1="9" y1="15" x2="9" y2="15.01" />
                <line x1="15" y1="13" x2="15" y2="13.01" />
                <line x1="15" y1="16" x2="15" y2="16.01" />
              </svg>
            </div>
            <h3 class="section-title">{{ t('admin-ui.company') }}</h3>
          </div>

          <div class="form-grid">
            <div class="input-group full-width">
              <hit-input
                name="userSystemLanguage"
                :value="
                  getLanguageDisplayName(objectFormData.userSystemLanguage)
                "
                :label="t('admin-ui.language_system')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="vatNumber"
                :value="objectFormData.vatNumber || ''"
                :label="t('admin-ui.vat_number')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="designationLegal"
                :value="objectFormData.designationLegal || ''"
                :label="t('admin-ui.legal_designation')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="street"
                :value="objectFormData.street || ''"
                :label="t('admin-ui.street')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="houseNumber"
                :value="objectFormData.houseNumber || ''"
                :label="t('admin-ui.label_house_number')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="boxNumber"
                :value="objectFormData.boxNumber || ''"
                :label="t('admin-ui.box_number')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="postCode"
                :value="objectFormData.postCode || ''"
                :label="t('admin-ui.post_code')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="city"
                :value="objectFormData.city || ''"
                :label="t('admin-ui.city')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="phone"
                :value="objectFormData.phone || ''"
                :label="t('admin-ui.phone')"
                :disabled="true"
              />
            </div>

            <div class="input-group">
              <hit-input
                name="mobile"
                :value="objectFormData.mobile || t('admin-ui.not_provided')"
                :label="t('admin-ui.mobile')"
                :disabled="true"
              />
            </div>

            <div class="input-group full-width">
              <hit-input
                name="activity"
                :value="activityDisplayValue"
                :label="t('admin-ui.activity')"
                :disabled="true"
              />
            </div>

            <div
              v-if="enableReferralSystem === 'true'"
              class="input-group full-width"
            >
              <hit-input
                name="referralCode"
                :value="
                  objectFormData.referralCode || t('admin-ui.not_provided')
                "
                :label="t('admin-ui.referral_code')"
                :disabled="true"
              />
              <div
                v-if="
                  objectFormData.referralCode &&
                  referralIsValid &&
                  referralFreeMonths !== null &&
                  referralFreeMonths !== undefined
                "
                class="referral-status"
              >
                <div class="referral-badge">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <span>
                  {{
                    t('admin-ui.referral-free-months', {
                      months: referralFreeMonths,
                    })
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog -->
    <div v-if="finalApiMakingRequest">
      <!-- Modern Account Creation Popup -->
      <div
        class="account-creation-overlay fixed inset-0 z-[9999] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      >
        <div
          class="account-creation-modal bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
        >
          <!-- Header with animated icon -->
          <div
            class="text-center p-8"
            :class="{'pb-6': !finalApiIsError, 'pb-2': finalApiIsError}"
          >
            <div class="account-creation-icon mx-auto mb-6">
              <div class="icon-circle">
                <svg
                  class="icon-svg"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g transform="translate(-27.164465,-22.917748)">
                    <path
                      d="m 0,0 h -2.667 c -2.37,0 -4.292,-1.922 -4.292,-4.292 v -40.531 c 9.254,-5.642 15.466,-15.801 15.466,-27.409 0,-11.609 -6.212,-21.766 -15.466,-27.408 v -40.531 c 0,-2.37 1.922,-4.292 4.292,-4.292 h 34.965 c 2.37,0 4.292,1.922 4.292,4.292 V -36.59 C 36.59,-16.382 20.208,0 0,0"
                      fill="currentColor"
                      transform="matrix(0.35277776,0,0,-0.35277776,65.722366,27.436081)"
                    />
                    <path
                      d="M 0,0 C 0,11.606 6.209,21.762 15.459,27.405 V 67.94 c 0,2.371 -1.922,4.292 -4.292,4.292 h -34.959 c -2.37,0 -4.292,-1.921 -4.292,-4.292 v -96.626 c 0,-22.419 16.942,-40.882 38.722,-43.281 2.561,-0.282 4.821,1.715 4.821,4.292 v 40.272 C 6.209,-21.761 0,-11.605 0,0"
                      fill="currentColor"
                      transform="matrix(0.35277776,0,0,-0.35277776,46.067715,52.959801)"
                    />
                    <path
                      d="m 0,0 c -3.539,5.468 -9.662,9.112 -16.645,9.112 -7.015,0 -13.158,-3.682 -16.689,-9.193 -1.976,-3.086 -3.159,-6.728 -3.159,-10.657 0,-3.927 1.183,-7.57 3.159,-10.655 3.531,-5.51 9.674,-9.193 16.689,-9.193 6.983,0 13.106,3.645 16.645,9.112 2.007,3.101 3.203,6.776 3.203,10.736 C 3.203,-6.776 2.007,-3.101 0,0"
                      fill="currentColor"
                      transform="matrix(0.35277776,0,0,-0.35277776,63.267456,49.129973)"
                    />
                  </g>
                </svg>
              </div>
            </div>

            <h3 class="account-creation-title">
              {{ finalApiTitleToShow }}
            </h3>

            <div v-if="finalApiIsError" class="error-message-card" role="alert">
              <div class="error-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path
                    d="M12 9v4m0 4h.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    fill="currentColor"
                    opacity="0.12"
                  />
                  <path
                    d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                  />
                </svg>
              </div>
              <div class="error-text">
                {{ finalApiMessageToShow }}
              </div>
            </div>
            <p v-else class="account-creation-subtitle">
              {{ t('admin-ui.account-creation-popup-subtitle') }}
            </p>
          </div>

          <!-- Content -->
          <div class="px-8 pb-6">
            <div v-if="!finalApiIsError" class="account-creation-content">
              <div v-if="!finalApiIsError" class="feature-list">
                <div class="feature-item">
                  <div class="feature-icon bg-blue-100 text-blue-600">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>{{
                    t('admin-ui.account-creation-popup-feature-creation')
                  }}</span>
                </div>

                <div class="feature-item">
                  <div class="feature-icon bg-green-100 text-green-600">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <polyline
                        points="22,6 12,13 2,6"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>{{
                    t('admin-ui.account-creation-popup-feature-email')
                  }}</span>
                </div>

                <div class="feature-item">
                  <div class="feature-icon bg-purple-100 text-purple-600">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="2"
                      />
                      <polyline
                        points="12,6 12,12 16,14"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>{{
                    t('admin-ui.account-creation-popup-feature-timing')
                  }}</span>
                </div>
              </div>

              <div v-if="!finalApiIsError" class="email-notification">
                <div class="success-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div class="email-content">
                  <h4 class="email-title">
                    {{ t('admin-ui.request-sent-successfully') }}
                  </h4>
                  <p class="email-text">
                    {{
                      t('admin-ui.account-creation-email-notification-merged')
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div v-if="finalApiShowConfirmButton" class="px-8 pb-8">
            <button
              class="close-button w-full"
              @click="finalApiMakingRequest = false"
            >
              {{ t('admin-ui.to_close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {MyUserCompany} from '../../objects/UserCompany';
import {defineComponent, PropType} from 'vue';
import {useI18n} from 'vue-i18n';
import HitInput from '@hit/components/src/components/form/input/HitInput.vue';
import HitForm from '@hit/components/src/components/form/HitForm.vue';
import HitBaseInput from '@hit/components/src/components/form/input/HitBaseInput.vue';
import {HitButton, HitDialog, HitGroupInput} from '@hit/components';
import HitPanel from '@hit/components/src/components/panel/HitPanel.vue';
import TopBarNavigation from '../TopBarNavigation.vue';
import axios from 'axios';
import {Base64} from 'js-base64';
import {HitLanguageUtils} from '@hit/base/src/utils';
import HitCountryFlag from '@hit/components/src/components/icon/HitCountryFlag.vue';
import HitSelect from '@hit/components/src/components/form/input/HitSelect.vue';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';

export default defineComponent({
  name: 'SummaryForm',
  computed: {
    HitLanguageUtils() {
      return HitLanguageUtils;
    },
    HitUtils() {
      return HitUtils;
    },
    isAccountCreationInProgress() {
      return (
        this.companyCreationIsPending ||
        (this.finalApiMakingRequest && !this.finalApiIsError)
      );
    },
    parsedActivities(): any[] {
      if (!this.objectFormData?.activity) return [];
      try {
        return JSON.parse(this.objectFormData.activity);
      } catch (error) {
        console.error('Error parsing activities:', error);
        return [];
      }
    },
    activityDisplayValue(): string {
      if (!this.parsedActivities.length) {
        return this.t('admin-ui.not_provided');
      }
      return this.parsedActivities
        .map((activity: any) => activity.designation)
        .filter(Boolean)
        .join(', ');
    },
  },
  components: {
    HitSelect,
    HitCountryFlag,
    HitDialog,
    TopBarNavigation,
    HitButton,
    HitGroupInput,
    HitForm,
    HitInput,
    HitBaseInput,
    HitPanel,
  },
  props: {
    myObject: {
      type: Object as PropType<MyUserCompany>,
      required: true,
    },
  },
  emits: ['previousClick', 'accountCreationStateChange'],
  watch: {
    isAccountCreationInProgress: {
      handler(value: boolean) {
        this.$emit('accountCreationStateChange', value);
      },
      immediate: true,
    },
  },
  setup() {
    const {t, locale} = useI18n();
    return {t, locale};
  },
  data() {
    return {
      nbrModuleSelected: 0,
      finalApiMakingRequest: false,
      finalApiTitleToShow: '',
      finalApiMessageToShow: '',
      finalApiLabelConfirmButton: '',
      finalApiShowConfirmButton: false,
      finalApiAllowToLeaveDialog: true,
      finalApiIsError: false,
      disableCreateAccountButton: false,
      companyCreationIsPending: false,
      languages: {},
      enableReferralSystem: process.env.VUE_APP_ENABLE_REFERRAL_SYSTEM,
      referralIsValid: false,
      referralFreeMonths: null as number | null,
      objectFormData: {
        usernameEmail: null as string | null,
        firstName: null as string | null,
        lastName: null as string | null,
        vatNumber: null as string | null,
        designationLegal: null as string | null,
        street: null as string | null,
        houseNumber: null as string | null,
        boxNumber: null as string | null,
        postCode: null as string | null,
        city: null as string | null,
        billingEmail: null as string | null,
        phone: null as string | null,
        mobile: null as string | null,
        userSystemLanguage: null as string | null,
        activity: null as string | null,
        referralCode: null as string | null,
      },
    };
  },
  mounted() {
    this.languages = HitLanguageUtils.getSupportedLanguageOptions();

    // Batch data updates to minimize reactive triggers
    Object.assign(this.objectFormData, {
      usernameEmail: this.myObject.usernameEmail || null,
      firstName: this.myObject.firstName || null,
      lastName: this.myObject.lastName || null,
      vatNumber: this.myObject.vatNumber || null,
      designationLegal: this.myObject.designationLegal || null,
      street: this.myObject.street || null,
      houseNumber: this.myObject.houseNumber || null,
      boxNumber: this.myObject.boxNumber || null,
      postCode: this.myObject.postCode || null,
      city: this.myObject.city || null,
      billingEmail: this.myObject.billingEmail || null,
      phone: this.myObject.phone || null,
      mobile: this.myObject.mobile || null,
      activity: this.myObject.activity || null,
      userSystemLanguage: this.myObject.userSystemLanguage || null,
      referralCode: this.myObject.referralCode || null,
    });

    if (
      this.enableReferralSystem === 'true' &&
      this.objectFormData.referralCode
    ) {
      this.fetchReferralDetails(this.objectFormData.referralCode as string);
    }
  },
  methods: {
    getLanguageDisplayName(languageCode: string | null) {
      if (!languageCode) return 'Non défini';

      const language = HitLanguageUtils.getSupportedLanguageOptions().find(
        (lang: any) =>
          lang.id === languageCode ||
          lang.id === HitUtils.kebabToSnake(languageCode),
      );

      return language ? language.designation : languageCode;
    },
    convertLocale(locale: any) {
      if (locale) {
        return locale.toLowerCase().replace('-', '_');
      }
    },
    async createAccount() {
      const token = this.generateAdminApiBase64Token();
      const vatNumber =
        typeof this.myObject.vatNumber === 'string'
          ? this.myObject.vatNumber.trim().toUpperCase()
          : this.myObject.vatNumber;

      this.companyCreationIsPending = true;

      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        params: {
          vat_number: vatNumber,
        },
      };

      let route = 'company/check/already/registered';
      let url = process.env.VUE_APP_ADMIN_API_BASE_URL + route;

      try {
        const response = await axios.get(url, config);

        if (response.data === true) {
          this.finalApiTitleToShow = this.t(
            'admin-ui.error-during-company-creation',
          );
          this.finalApiMessageToShow = this.t(
            'admin-ui.vat-number-already-linked-to-a-company',
          );

          this.companyCreationIsPending = false;
          this.finalApiLabelConfirmButton = this.t('admin-ui.confirm');
          this.finalApiShowConfirmButton = true;
          this.finalApiIsError = true;
          this.finalApiMakingRequest = true;
        } else {
          this.finalApiTitleToShow = this.t(
            'admin-ui.account-creation-popup-title-creating',
          );
          this.finalApiMessageToShow = this.t(
            'admin-ui.account-creation-popup-subtitle',
          );
          this.finalApiLabelConfirmButton = this.t('admin-ui.confirm');
          this.finalApiShowConfirmButton = false;

          this.finalApiMakingRequest = true;
          route = 'company/deposit/create-account';
          url = process.env.VUE_APP_ADMIN_API_BASE_URL + route;

          const payload = {
            user_data: {
              first_name: this.myObject.firstName,
              last_name: this.myObject.lastName,
              username_email: this.myObject.usernameEmail?.toLowerCase(),
              email: this.myObject.usernameEmail?.toLowerCase(),
              password: this.myObject.password,
              admin: true,
              locale: this.convertLocale(this.myObject.userSystemLanguage),
              create_staff_entry: true,
            },
            company_data: {
              designation: this.myObject.designationLegal,
              vat_number: vatNumber,
              phone: this.myObject.phone,
              mobile: this.myObject.mobile,
              billing_email: this.myObject.billingEmail?.toLowerCase(),
              street: this.myObject.street,
              house_no: this.myObject.houseNumber,
              box_no: this.myObject.boxNumber,
              post_code: this.myObject.postCode,
              country: this.myObject.countryCode,
              city: this.myObject.city,
              activity: this.parsedActivities,
              language: this.convertLocale(this.myObject.userSystemLanguage), // fr-BE -> fr_be
              referrer_code: this.myObject.referralCode,
            },
          };

          try {
            await axios.post(url, payload, config);
            this.finalApiTitleToShow = this.t(
              'admin-ui.account-creation-popup-title-created',
            );
            this.finalApiMessageToShow = this.t(
              'admin-ui.account-creation-popup-email-ready',
            );
            this.finalApiIsError = false;
            this.disableCreateAccountButton = true;
          } catch (error) {
            this.finalApiTitleToShow = this.t(
              'admin-ui.error-during-company-creation',
            );
            this.finalApiMessageToShow = this.t(
              'admin-ui.error-company-was-not-set-up-successfully',
            );
            this.finalApiIsError = true;
          }

          this.finalApiShowConfirmButton = true;
          this.finalApiAllowToLeaveDialog = true;
          this.companyCreationIsPending = false;
        }
      } catch (error) {
        this.companyCreationIsPending = false;
        this.finalApiTitleToShow = this.t(
          'admin-ui.error-during-company-creation',
        );
        this.finalApiMessageToShow = this.t(
          'admin-ui.error-company-was-not-set-up-successfully',
        );
        this.finalApiShowConfirmButton = true;
        this.finalApiIsError = true;
        this.finalApiMakingRequest = true;
      }
    },
    generateAdminApiBase64Token(): string {
      const concatCredentials: string = `${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME}:${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD}`;
      return Base64.encode(concatCredentials);
    },
    async fetchReferralDetails(referralCode: string) {
      if (!referralCode || referralCode.length !== 6) {
        this.referralIsValid = false;
        this.referralFreeMonths = null;
        return;
      }
      const token = this.generateAdminApiBase64Token();
      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        params: {referral_code: referralCode},
      };
      const route = 'referral/check';
      const url = process.env.VUE_APP_ADMIN_API_BASE_URL + route;
      try {
        const response = await axios.get(url, config);
        if (typeof response.data === 'boolean') {
          this.referralIsValid = response.data;
          this.referralFreeMonths = response.data ? 2 : null;
        } else {
          this.referralIsValid = !!response.data?.valid;
          this.referralFreeMonths =
            typeof response.data?.freeMonths === 'number'
              ? response.data.freeMonths
              : null;
        }
      } catch (e) {
        this.referralIsValid = false;
        this.referralFreeMonths = null;
      }
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 1rem 1rem 1rem;
  background: #f1f5f9;
  position: relative;
}

.form-card {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0;
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  border: 1px solid #e5e7eb;
}

.card-header {
  background: #0d3749;
  color: white;
  padding: 1.5rem;
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  margin-bottom: 0.75rem;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin: 0 0 0.375rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.card-body {
  padding: 1.5rem;
}

.info-section {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  margin-bottom: 1.25rem;
  overflow: hidden;
  background: #ffffff;
}

.section-header {
  background: #f9fafb;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
}

.user-icon,
.company-icon {
  background: #6b7280;
  color: white;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.form-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr 1fr;
  padding: 1.25rem;
}

.input-group {
  position: relative;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.referral-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0369a1;
}

.referral-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #0ea5e9;
  color: white;
}

.action-section {
  background: linear-gradient(
    135deg,
    rgba(13, 55, 73, 0.08) 0%,
    rgba(26, 188, 156, 0.08) 100%
  );
  border-radius: 1.25rem;
  padding: 2rem;
  border: 3px solid rgba(13, 55, 73, 0.15);
  text-align: center;
  position: relative;
  box-shadow: 0 8px 25px rgba(13, 55, 73, 0.15);
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.action-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #0d3749, #1abc9c, #00b391);
  border-radius: 1.25rem 1.25rem 0 0;
}

.action-highlight {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.85);
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0d3749 0%, #1abc9c 100%);
  border-radius: 50%;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(13, 55, 73, 0.3);
}

.action-info {
  flex: 1;
  text-align: left;
}

.action-info h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #0d3749;
  margin: 0;
  line-height: 1.4;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.back-button,
.create-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border-radius: 0.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  min-width: 160px;
  justify-content: center;
}

.back-button {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.back-button:hover:not(:disabled) {
  background: #e5e7eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.back-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.create-button {
  background: linear-gradient(135deg, #0d3749 0%, #1abc9c 100%);
  color: white;
  box-shadow: 0 6px 20px rgba(13, 55, 73, 0.4);
  position: relative;
  overflow: hidden;
}

.create-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.6s;
}

.create-button:hover::before {
  left: 100%;
}

.create-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(13, 55, 73, 0.5);
  background: linear-gradient(135deg, #1abc9c 0%, #00b391 100%);
}

.create-button:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.create-button:disabled::before {
  display: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (min-width: 768px) {
  .modern-form-container {
    padding: 4rem 1rem 1rem 1rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .form-subtitle {
    font-size: 1rem;
  }

  .card-header {
    padding: 1.75rem;
  }

  .card-body {
    padding: 1.75rem;
  }

  .action-section {
    padding: 2.5rem;
  }
}

@media (max-width: 768px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.5rem 1rem 0.25rem 1rem;
    justify-content: center;
  }

  .form-card {
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }

  .section-header {
    padding: 0.875rem 1rem;
  }

  .action-section {
    padding: 1.25rem;
  }

  .action-highlight {
    flex-direction: column;
    text-align: center;
    padding: 1.25rem;
    gap: 0.75rem;
  }

  .action-info {
    text-align: center;
  }

  .action-info h4 {
    font-size: 1rem;
  }

  .action-icon {
    width: 40px;
    height: 40px;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .back-button,
  .create-button {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    min-height: auto;
    padding: 0.25rem 0.75rem 0.125rem 0.75rem;
  }

  .form-card {
    border-radius: 0.5rem;
  }

  .card-header {
    padding: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.125rem;
  }

  .form-subtitle {
    font-size: 0.875rem;
  }

  .header-icon {
    width: 36px;
    height: 36px;
  }

  .form-grid {
    padding: 0.75rem;
    gap: 0.75rem;
  }
}

/* Focus states and accessibility */
.back-button:focus,
.create-button:focus {
  outline: 2px solid #0d3749;
  outline-offset: 2px;
}

/* Modern Account Creation Modal Styles */
.account-creation-overlay {
  z-index: 99999 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
}

.account-creation-modal {
  animation: modalSlideIn 0.3s ease-out;
  position: relative;
  z-index: 100000 !important;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.account-creation-icon {
  position: relative;
  width: 80px;
  height: 80px;
}

.icon-circle {
  position: relative;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0d3749 0%, #1abc9c 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: iconGlow 2s ease-in-out infinite alternate;
}

.icon-svg {
  width: 32px;
  height: 32px;
  color: white;
  z-index: 2;
  position: relative;
}

@keyframes iconGlow {
  from {
    box-shadow: 0 0 20px rgba(13, 55, 73, 0.4);
  }
  to {
    box-shadow:
      0 0 30px rgba(13, 55, 73, 0.6),
      0 0 40px rgba(26, 188, 156, 0.2);
  }
}

.account-creation-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.account-creation-subtitle {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

.error-message-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  margin: 0;
}

.error-icon {
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.error-text {
  font-size: 0.95rem;
  font-weight: 500;
}

.account-creation-content {
  background: #f8fafc;
  border-radius: 1rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.feature-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.email-notification {
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%);
  border: 2px solid #00997c;
  border-radius: 1rem;
  padding: 1.25rem;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: successSlideIn 0.5s ease-out;
}

.email-notification::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #00997c, #1abc9c, #00b391);
  animation: emailNotificationGlow 2s ease-in-out infinite;
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #00997c;
  border-radius: 50%;
  color: white;
  flex-shrink: 0;
  animation: successPulse 2s ease-in-out infinite;
}

.email-content {
  flex: 1;
}

.email-title {
  font-size: 1rem;
  font-weight: 700;
  color: #134e4a;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.email-text {
  font-size: 0.875rem;
  color: #0f766e;
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
}

@keyframes emailNotificationGlow {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes successSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes successPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 153, 124, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 8px rgba(0, 153, 124, 0);
  }
}

@keyframes actionSectionPulse {
  0%,
  100% {
    transform: translateY(-10px) scale(1);
    box-shadow: 0 8px 25px rgba(13, 55, 73, 0.15);
  }
  50% {
    transform: translateY(-12px) scale(1.005);
    box-shadow: 0 12px 35px rgba(13, 55, 73, 0.2);
  }
}

@keyframes actionGlow {
  0%,
  100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

@keyframes iconBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.close-button {
  background: linear-gradient(135deg, #00997c 0%, #1abc9c 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(13, 55, 73, 0.2);
}

.close-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 55, 73, 0.3);
  background: linear-gradient(135deg, #00997c 0%, #1abc9c 100%);
  opacity: 0.9;
}

.close-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(13, 55, 73, 0.2);
}

/* Responsive adjustments for mobile */
@media (max-width: 640px) {
  .account-creation-modal {
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }

  .account-creation-icon {
    width: 64px;
    height: 64px;
  }

  .icon-circle {
    width: 64px;
    height: 64px;
  }

  .icon-svg {
    width: 24px;
    height: 24px;
  }

  .account-creation-title {
    font-size: 1.25rem;
  }

  .account-creation-subtitle {
    font-size: 0.875rem;
  }

  .account-creation-content {
    padding: 1rem;
  }

  .feature-item {
    font-size: 0.8rem;
  }

  .feature-icon {
    width: 28px;
    height: 28px;
  }

  .email-text {
    font-size: 0.8rem;
  }

  .email-notification {
    padding: 1rem;
    gap: 0.75rem;
  }

  .success-icon {
    width: 36px;
    height: 36px;
  }

  .email-title {
    font-size: 0.9rem;
  }
}
</style>
